//------------------

import { filter, isString, keys } from "lodash";

export interface PaginationInfo {
  pageIndex: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  sort?: string | null;
  filter?: string | null;  
  downloadType?: string | null;
}

// Handle a blob/arraybuffer response and trigger the download
const CD_HEADER = "content-disposition";
const CD_HEADER_CAP = "Content-Disposition";    //historically, it has come back both ways

//Gets the body fields that are blobs
const getBlobFields = (body: Record<string, any>) => {
  if(!body) return false;
  const imgFields = filter(keys(body), key => body[key] instanceof Blob);
  return imgFields;
}

//--
// Will prepare the body to be sent to the server
// This includes checking for images and using FormData if necessary.
// Otherwise, returns a stringified version of the body (if it's not already a string)
const prepareBody = (body: any) => {
  if(!body) return body;
  else if(isString(body)) return body;
  
  const blobFields = getBlobFields(body);  
  if(blobFields && blobFields.length > 0){
    //This body has one or more image files, so need to use formData
    let formData = new FormData();
    //enumerate the image fields, append them to the formData and remove them from the body object
    blobFields.forEach(id => {
      formData.append(id, body[id] || new Blob());
      delete body[id];
    });

    //add the remainder of the body as a payload
    formData.append("payload", JSON.stringify(body));
    return formData;
  }
  else return JSON.stringify(body);  
}

//--
// performs a fetch, and gets a JSON result
export async function doFetch(url: string, method: string, body: any) {

  try {
    const bodyData = prepareBody(body);
    const result = await fetch(url, { method: method, body: bodyData });

    if (result.json) {
      const json = await result.json();

      if (!result.ok) {
        return {
          isError: true,
          status: result.status,
          message: result.statusText,
          ...json
        };
      }

      return json;  //all good, just return the result
    }
    else if (result.ok === true) {
      //Nothing to return, but all is good
      return result;
    }
  }
  catch (err: any) {
    let errMsg = "Unhandled exception during fetch. Our technical team has been notified.";
    if (err.text) errMsg = await err.text();

    return {
      isError: true,
      message: errMsg
    };
  }
}

//--
// performs a fetch with the provided bearer token, and gets a JSON result
export async function doFetchWithToken(url: string, method: string, body: any, token: string) {

  try {
    const headers = {
      "Authorization": "Bearer " + token,
      "x-functions-key" : token //Used by Azure functions, same as ?code={token}
    };


    const bodyData = prepareBody(body);
    const result = await fetch(url, { method: method, body: bodyData, headers });

    if (result.json) {
      const json = await result.json();

      if (!result.ok) {
        return {
          isError: true,
          status: result.status,
          message: result.statusText,
          ...json
        };
      }

      return json;  //all good, just return the result
    }
    else if (result.ok === true) {
      //Nothing to return, but all is good
      return result;
    }
  }
  catch (err: any) {
    let errMsg = "Unhandled exception during fetch. Our technical team has been notified.";
    if (err.text) errMsg = await err.text();

    return {
      isError: true,
      message: errMsg
    };
  }
}


export async function doFileFetchWithToken(url: string, method: string, body: any, token: string, contentType: string) {
  try {
    const headers = {
      "Authorization": "Bearer " + token,
    };

    const result = await fetch(url, { method: method, body, headers });
    if (result.ok) {
      const isOk = await downloadFile(result, contentType);
      if (!isOk) {
        return {
          isError: true,
          // status: result.status,
          message: result.statusText,
          ...result,
        };
      }
      else {
        return {
          isError: false,
          ...result,
        }
      }
    }
    else {
      return {
        isError: true,
        error: result.statusText || "Failed to fetch file for download",
        ...result,
      }
    }
  }
  catch (err: any) {
    let errMsg = "Unhandled exception during fetch. Our technical team has been notified.";
    if (err.text) errMsg = await err.text();

    return {
      isError: true,
      error: errMsg
    };
  }
}


export async function downloadFile(result: Response, mimeType: string) {
  const cd = result.headers.get(CD_HEADER) || result.headers.get(CD_HEADER_CAP);

  if (cd) {
    //The file to download was returned as a Blob in the response.
    let fileName = null;
    const cdParts = cd.split(';');
    const fname = cdParts.find(cdp => cdp.indexOf("filename=") >= 0);
    if (fname) {
      fileName = fname.trim().substr("filename=".length);
    }

    if (fileName && mimeType) {
      var linkElement = document.createElement('a');
      try {
        var data = await result.blob();
        var blob = new Blob([data], { type: mimeType });
        var url = window.URL.createObjectURL(blob);

        linkElement.setAttribute('href', url);
        linkElement.setAttribute("download", fileName);

        var clickEvent = new MouseEvent("click", {
          "view": window,
          "bubbles": true,
          "cancelable": false
        });
        linkElement.dispatchEvent(clickEvent);

        return true;    //indicate it worked

      } catch (ex) {
        console.log(ex);
      }
    }
  }

  return false;
}

export function parsePagination(data: Record<string, any>){
  const pagination : PaginationInfo = {
    hasNextPage: data.hasNextPage,
    hasPreviousPage: data.hasPreviousPage,
    pageIndex: data.pageIndex,
    totalCount: data.totalCount,
    totalPages: data.totalPages,
    filter: data.filter,
    sort: data.sort,
  };

  return pagination;
}


export function logApiError(error: Error) {
  if (!error) {
      return false;
  } 
  else {
    if (error instanceof window.Response) {
        console.log(`error.status: ${error.status}\r\n error.url: ${error.url}\r\n error.statusText: ${error.statusText}`);
    } else {
        console.log(`error: ${error.toString()}`);
    }

    return true;
  }
}